import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';

import { FacetSelectionType } from '../../../profile-facets/entities/facet-request.entity';
import { ShIconModule } from '../icon/icon.module';
import { ShFacet, ShFacetValue, ShSelectableFacetValue, ShSelectedFacetValue } from './entities/facet.entity';
import { ShFacetValueLabelPipe } from './pipes/facet-value-label.pipe';

export * from './entities/facet.entity';
@Component({
    standalone: true,
    selector: 'sh-facet',
    templateUrl: './facet.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShIconModule, TranslateModule, ShFacetValueLabelPipe]
})
export class ShFacetComponent extends BaseContainer implements OnInit {
    private readonly blockShowMore$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly noValueKey: string = 'overrides.facets.no-value';
    public facet$: ReplaySubject<ShFacet | undefined> = new ReplaySubject(1);
    public showLoadMore = false;
    public ShFacetSelectionType: typeof FacetSelectionType = FacetSelectionType;

    @Input() set facet(facet: ShFacet | undefined) {
        this.facet$.next(facet);
    }

    @Input() showLabel = true;

    @Input() showMoreMsg = 'Show more';
    @Input() set blockShowMore(blockShowMore: boolean) {
        this.blockShowMore$.next(blockShowMore);
    }

    // Do not remove. Used to generate different ids for dialog and list facet values
    // Removing this will trigger unwanted behaviour.
    @Input() facetDialogOpen = false;

    @Output() selectionChanged: EventEmitter<ShFacet> = new EventEmitter<ShFacet>();
    @Output() valueSelected: EventEmitter<ShSelectedFacetValue> = new EventEmitter<ShSelectedFacetValue>();

    @Output() loadMore: EventEmitter<ShFacet> = new EventEmitter<ShFacet>();

    trackByLabel(index: number, facetValue: ShFacetValue): string {
        return facetValue.label;
    }

    ngOnInit(): void {
        this.initShouldShowMoreListener();
    }

    private initShouldShowMoreListener(): void {
        combineLatest([this.facet$, this.blockShowMore$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([facet, blockShowMore]) => {
                if (!facet || blockShowMore) {
                    this.showLoadMore = false;
                    return;
                }

                if (facet.hasMoreValues) {
                    this.showLoadMore = true;
                    return;
                }

                // If facet is included in api response but without values, we force the 'load more' button to be visible
                if (!facet.hasMoreValues && facet.values.length === 0) {
                    this.showLoadMore = true;
                    return;
                }

                this.showLoadMore = false;
            });
    }

    public onShowMore(facet: ShFacet): void {
        this.loadMore.emit(facet);
    }

    public onSelectionChange(facet: ShFacet, facetValue: ShFacetValue): void {
        const newValues: ShSelectableFacetValue[] = facet.values.map(fv => {
            if (fv.value === facetValue.value) {
                return { ...fv, selected: !fv.selected };
            }
            return fv;
        });

        const newFacet: ShFacet = { ...facet, values: newValues };

        this.selectionChanged.emit(newFacet);

        const valueSelected: ShSelectedFacetValue = {
            facetLabel: facet.label,
            key: facet.key,
            valueLabel: facetValue.label,
            value: facetValue.value,
            missing: facetValue.missing,
            selectionType: facet.selectionType,
            count: facetValue.count
        };

        this.valueSelected.emit(valueSelected);
    }
}
